import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';

export default function LandingPage() {
  const [activeComparison, setActiveComparison] = useState<'video' | 'code'>('video');
  const [visibleSections, setVisibleSections] = useState<Set<string>>(new Set());

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setVisibleSections((prev) => new Set([...prev, entry.target.id]));
          }
        });
      },
      { threshold: 0.1 }
    );

    document.querySelectorAll('section[id]').forEach((section) => {
      observer.observe(section);
    });

    return () => observer.disconnect();
  }, []);

  return (
    <div style={{ minHeight: '100vh', background: 'var(--black-900)' }}>
      {/* Navigation */}
      <nav
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          zIndex: 100,
          padding: 'var(--space-lg) var(--space-xl)',
          background: 'rgba(10, 10, 11, 0.8)',
          backdropFilter: 'blur(12px)',
          borderBottom: '1px solid rgba(39, 39, 42, 0.5)',
        }}
      >
        <div
          style={{
            maxWidth: 1280,
            margin: '0 auto',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-sm)' }}>
            <div
              style={{
                width: 36,
                height: 36,
                borderRadius: 'var(--radius-md)',
                background: 'linear-gradient(135deg, var(--cyan-500), var(--purple-500))',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <svg
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                stroke="white"
                strokeWidth="2.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <polygon points="23 7 16 12 23 17 23 7" />
                <rect x="1" y="5" width="15" height="14" rx="2" ry="2" />
              </svg>
            </div>
            <span style={{ fontSize: '1.25rem', fontWeight: 600, color: 'white', letterSpacing: '-0.02em' }}>
              VideoToCode
            </span>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-xl)' }}>
            <a href="#how-it-works" style={{ color: 'var(--black-200)', fontSize: '0.9375rem' }}>
              How It Works
            </a>
            <a href="#pricing" style={{ color: 'var(--black-200)', fontSize: '0.9375rem' }}>
              Pricing
            </a>
            <Link to="/dashboard" className="btn btn-secondary btn-sm">
              Sign In
            </Link>
            <Link to="/dashboard" className="btn btn-primary btn-sm">
              Start Free
            </Link>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section
        id="hero"
        style={{
          minHeight: '100vh',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          padding: 'calc(80px + var(--space-4xl)) var(--space-xl) var(--space-4xl)',
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        {/* Background gradient */}
        <div
          style={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: '150%',
            height: '150%',
            background: `
              radial-gradient(ellipse 80% 50% at 50% 20%, rgba(6, 214, 160, 0.08) 0%, transparent 50%),
              radial-gradient(ellipse 60% 40% at 30% 70%, rgba(139, 92, 246, 0.05) 0%, transparent 50%)
            `,
            pointerEvents: 'none',
          }}
        />

        {/* Floating code snippets decoration */}
        <FloatingCodeBlock
          code="interpolate(frame, [0, 30], [0, 100])"
          style={{ top: '20%', left: '10%', animationDelay: '0s' }}
        />
        <FloatingCodeBlock
          code="useCurrentFrame()"
          style={{ top: '35%', right: '8%', animationDelay: '1s' }}
        />
        <FloatingCodeBlock
          code="<AbsoluteFill>"
          style={{ bottom: '25%', left: '15%', animationDelay: '2s' }}
        />

        {/* Hero content */}
        <div
          style={{
            maxWidth: 900,
            textAlign: 'center',
            position: 'relative',
            zIndex: 1,
          }}
          className={visibleSections.has('hero') ? 'animate-fade-in' : ''}
        >
          <div
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 'var(--space-sm)',
              padding: 'var(--space-xs) var(--space-md)',
              background: 'var(--cyan-glow)',
              border: '1px solid rgba(6, 214, 160, 0.3)',
              borderRadius: 'var(--radius-2xl)',
              marginBottom: 'var(--space-xl)',
              fontSize: '0.875rem',
              color: 'var(--cyan-400)',
            }}
          >
            <span
              style={{
                width: 6,
                height: 6,
                borderRadius: '50%',
                background: 'var(--cyan-500)',
              }}
            />
            For Remotion Developers & Motion Designers
          </div>

          <h1
            style={{
              marginBottom: 'var(--space-lg)',
              background: 'linear-gradient(180deg, white 0%, var(--black-100) 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
            }}
          >
            Turn Any Video Into
            <br />
            <span style={{ color: 'var(--cyan-500)', WebkitTextFillColor: 'var(--cyan-500)' }}>
              Remotion Code
            </span>
          </h1>

          <p
            style={{
              fontSize: '1.25rem',
              color: 'var(--black-200)',
              maxWidth: 600,
              margin: '0 auto var(--space-2xl)',
              lineHeight: 1.7,
            }}
          >
            Upload a video clip, our AI analyzes every motion, color shift, and transition.
            Get production-ready Remotion code in seconds.
          </p>

          <div style={{ display: 'flex', gap: 'var(--space-md)', justifyContent: 'center' }}>
            <Link to="/dashboard" className="btn btn-primary btn-lg">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                <polyline points="17 8 12 3 7 8" />
                <line x1="12" y1="3" x2="12" y2="15" />
              </svg>
              Upload Your First Clip
            </Link>
            <a href="#how-it-works" className="btn btn-secondary btn-lg">
              See How It Works
            </a>
          </div>
        </div>

        {/* Video/Code Comparison */}
        <div
          style={{
            marginTop: 'var(--space-4xl)',
            width: '100%',
            maxWidth: 1000,
            position: 'relative',
          }}
          className={visibleSections.has('hero') ? 'animate-slide-up stagger-2' : ''}
        >
          {/* Toggle */}
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              marginBottom: 'var(--space-lg)',
            }}
          >
            <div
              style={{
                display: 'inline-flex',
                padding: 4,
                background: 'var(--black-800)',
                borderRadius: 'var(--radius-lg)',
                border: '1px solid var(--black-600)',
              }}
            >
              <button
                onClick={() => setActiveComparison('video')}
                style={{
                  padding: 'var(--space-sm) var(--space-lg)',
                  fontSize: '0.875rem',
                  fontWeight: 500,
                  border: 'none',
                  borderRadius: 'var(--radius-md)',
                  cursor: 'pointer',
                  transition: 'all var(--transition-fast)',
                  background: activeComparison === 'video' ? 'var(--black-600)' : 'transparent',
                  color: activeComparison === 'video' ? 'white' : 'var(--black-300)',
                }}
              >
                Original Video
              </button>
              <button
                onClick={() => setActiveComparison('code')}
                style={{
                  padding: 'var(--space-sm) var(--space-lg)',
                  fontSize: '0.875rem',
                  fontWeight: 500,
                  border: 'none',
                  borderRadius: 'var(--radius-md)',
                  cursor: 'pointer',
                  transition: 'all var(--transition-fast)',
                  background: activeComparison === 'code' ? 'var(--black-600)' : 'transparent',
                  color: activeComparison === 'code' ? 'white' : 'var(--black-300)',
                }}
              >
                Generated Code
              </button>
            </div>
          </div>

          {/* Comparison Display */}
          <div
            style={{
              position: 'relative',
              borderRadius: 'var(--radius-xl)',
              overflow: 'hidden',
              border: '1px solid var(--black-600)',
              background: 'var(--black-800)',
              aspectRatio: '16/9',
            }}
          >
            {/* Video placeholder */}
            <div
              style={{
                position: 'absolute',
                inset: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                opacity: activeComparison === 'video' ? 1 : 0,
                transition: 'opacity var(--transition-base)',
              }}
            >
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: 'var(--space-lg)',
                  color: 'var(--black-300)',
                }}
              >
                {/* Animated play button */}
                <div
                  style={{
                    width: 80,
                    height: 80,
                    borderRadius: '50%',
                    background: 'var(--black-700)',
                    border: '2px solid var(--black-500)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                  className="animate-glow"
                >
                  <svg
                    width="32"
                    height="32"
                    viewBox="0 0 24 24"
                    fill="var(--cyan-500)"
                    style={{ marginLeft: 4 }}
                  >
                    <polygon points="5 3 19 12 5 21 5 3" />
                  </svg>
                </div>
                <span>Sample animation video</span>
              </div>
            </div>

            {/* Code display */}
            <div
              style={{
                position: 'absolute',
                inset: 0,
                padding: 'var(--space-xl)',
                opacity: activeComparison === 'code' ? 1 : 0,
                transition: 'opacity var(--transition-base)',
                overflow: 'auto',
              }}
            >
              <pre
                style={{
                  fontFamily: 'var(--font-mono)',
                  fontSize: '0.8125rem',
                  lineHeight: 1.7,
                  color: 'var(--black-100)',
                  margin: 0,
                }}
              >
                <code>
                  {`import { useCurrentFrame, interpolate, AbsoluteFill } from 'remotion';

export const LogoBounce: React.FC = () => {
  const frame = useCurrentFrame();

  // Position interpolation from AI analysis
  const x = interpolate(frame, [0, 15, 30], [0, 200, 400], {
    extrapolateRight: 'clamp',
  });

  const y = interpolate(frame, [0, 15, 30], [100, 50, 100], {
    extrapolateRight: 'clamp',
  });

  // Scale with detected easing
  const scale = interpolate(frame, [0, 15, 30], [1, 1.2, 1]);

  return (
    <AbsoluteFill style={{ backgroundColor: '#0a0a0b' }}>
      <div
        style={{
          transform: \`translate(\${x}px, \${y}px) scale(\${scale})\`,
          width: 120,
          height: 120,
          borderRadius: 24,
          background: 'linear-gradient(135deg, #06d6a0, #8b5cf6)',
        }}
      />
    </AbsoluteFill>
  );
};`}
                </code>
              </pre>
            </div>

            {/* Scan line effect */}
            <div
              style={{
                position: 'absolute',
                inset: 0,
                background:
                  'repeating-linear-gradient(0deg, transparent, transparent 2px, rgba(0,0,0,0.03) 2px, rgba(0,0,0,0.03) 4px)',
                pointerEvents: 'none',
              }}
            />
          </div>
        </div>
      </section>

      {/* Pain Points Section */}
      <section
        id="pain-points"
        style={{
          padding: 'var(--space-4xl) var(--space-xl)',
          background: 'var(--black-800)',
          borderTop: '1px solid var(--black-700)',
          borderBottom: '1px solid var(--black-700)',
        }}
      >
        <div style={{ maxWidth: 1200, margin: '0 auto' }}>
          <div
            style={{ textAlign: 'center', marginBottom: 'var(--space-3xl)' }}
            className={visibleSections.has('pain-points') ? 'animate-fade-in' : ''}
          >
            <h2 style={{ marginBottom: 'var(--space-md)' }}>
              Recreating Animations is{' '}
              <span style={{ color: 'var(--red-400)' }}>Painful</span>
            </h2>
            <p style={{ color: 'var(--black-200)', fontSize: '1.125rem', maxWidth: 600, margin: '0 auto' }}>
              You've seen the perfect animation. Now you need to build it from scratch.
            </p>
          </div>

          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
              gap: 'var(--space-xl)',
            }}
          >
            <PainPointCard
              icon={
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="12" cy="12" r="10" />
                  <polyline points="12 6 12 12 16 14" />
                </svg>
              }
              title="Hours of Manual Work"
              description="Pausing, measuring, guessing easing curves. Frame by frame. Over and over."
              visible={visibleSections.has('pain-points')}
              delay={0}
            />
            <PainPointCard
              icon={
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z" />
                </svg>
              }
              title="Imprecise Results"
              description="Eyeballing timing never matches the original. Something always feels off."
              visible={visibleSections.has('pain-points')}
              delay={1}
            />
            <PainPointCard
              icon={
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z" />
                  <polyline points="3.27 6.96 12 12.01 20.73 6.96" />
                  <line x1="12" y1="22.08" x2="12" y2="12" />
                </svg>
              }
              title="No Searchable Library"
              description="Your animation collection is scattered across folders. Finding the right reference takes forever."
              visible={visibleSections.has('pain-points')}
              delay={2}
            />
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section
        id="how-it-works"
        style={{
          padding: 'var(--space-4xl) var(--space-xl)',
        }}
      >
        <div style={{ maxWidth: 1200, margin: '0 auto' }}>
          <div
            style={{ textAlign: 'center', marginBottom: 'var(--space-3xl)' }}
            className={visibleSections.has('how-it-works') ? 'animate-fade-in' : ''}
          >
            <h2 style={{ marginBottom: 'var(--space-md)' }}>
              Three Steps to <span style={{ color: 'var(--cyan-500)' }}>Perfect Code</span>
            </h2>
            <p style={{ color: 'var(--black-200)', fontSize: '1.125rem', maxWidth: 600, margin: '0 auto' }}>
              From video to production-ready Remotion in under a minute.
            </p>
          </div>

          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(3, 1fr)',
              gap: 'var(--space-2xl)',
              position: 'relative',
            }}
          >
            {/* Connecting line */}
            <div
              style={{
                position: 'absolute',
                top: 60,
                left: '20%',
                right: '20%',
                height: 2,
                background: 'linear-gradient(90deg, var(--black-600), var(--cyan-500), var(--black-600))',
                zIndex: 0,
              }}
            />

            <StepCard
              number={1}
              title="Upload"
              description="Drag and drop any video clip. Trim to the exact segment you want to recreate."
              icon={
                <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                  <polyline points="17 8 12 3 7 8" />
                  <line x1="12" y1="3" x2="12" y2="15" />
                </svg>
              }
              visible={visibleSections.has('how-it-works')}
              delay={0}
            />
            <StepCard
              number={2}
              title="Analyze"
              description="AI detects motion paths, color changes, easing curves, and timing with frame-level precision."
              icon={
                <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <polyline points="22 12 18 12 15 21 9 3 6 12 2 12" />
                </svg>
              }
              visible={visibleSections.has('how-it-works')}
              delay={1}
            />
            <StepCard
              number={3}
              title="Generate"
              description="Get clean, commented Remotion code. Copy, paste, and you're done."
              icon={
                <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <polyline points="16 18 22 12 16 6" />
                  <polyline points="8 6 2 12 8 18" />
                </svg>
              }
              visible={visibleSections.has('how-it-works')}
              delay={2}
            />
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section
        id="pricing"
        style={{
          padding: 'var(--space-4xl) var(--space-xl)',
          background: 'var(--black-800)',
          borderTop: '1px solid var(--black-700)',
        }}
      >
        <div style={{ maxWidth: 900, margin: '0 auto' }}>
          <div
            style={{ textAlign: 'center', marginBottom: 'var(--space-3xl)' }}
            className={visibleSections.has('pricing') ? 'animate-fade-in' : ''}
          >
            <h2 style={{ marginBottom: 'var(--space-md)' }}>Simple, Transparent Pricing</h2>
            <p style={{ color: 'var(--black-200)', fontSize: '1.125rem' }}>
              Start free. Upgrade when you need more.
            </p>
          </div>

          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(2, 1fr)',
              gap: 'var(--space-xl)',
            }}
          >
            <PricingCard
              name="Free"
              price="$0"
              period="/month"
              description="Perfect for trying out VideoToCode"
              features={['5 clips per month', 'Basic analysis', 'Remotion code export', 'Community support']}
              buttonText="Get Started"
              buttonVariant="secondary"
              visible={visibleSections.has('pricing')}
              delay={0}
            />
            <PricingCard
              name="Pro"
              price="$19"
              period="/month"
              description="For serious motion designers"
              features={[
                '100 clips per month',
                'Advanced analysis',
                'Priority processing',
                'Animation library search',
                'Export presets',
                'Email support',
              ]}
              buttonText="Start Pro Trial"
              buttonVariant="primary"
              highlighted
              visible={visibleSections.has('pricing')}
              delay={1}
            />
          </div>
        </div>
      </section>

      {/* Final CTA Section */}
      <section
        id="cta"
        style={{
          padding: 'var(--space-4xl) var(--space-xl)',
          textAlign: 'center',
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        {/* Background glow */}
        <div
          style={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: 600,
            height: 600,
            background: 'radial-gradient(circle, var(--cyan-glow) 0%, transparent 70%)',
            pointerEvents: 'none',
          }}
        />

        <div
          style={{ position: 'relative', zIndex: 1 }}
          className={visibleSections.has('cta') ? 'animate-fade-in' : ''}
        >
          <h2 style={{ marginBottom: 'var(--space-md)' }}>
            Ready to Transform Your Workflow?
          </h2>
          <p
            style={{
              color: 'var(--black-200)',
              fontSize: '1.125rem',
              marginBottom: 'var(--space-2xl)',
              maxWidth: 500,
              marginLeft: 'auto',
              marginRight: 'auto',
            }}
          >
            Join motion designers who are saving hours on every project.
          </p>
          <Link to="/dashboard" className="btn btn-primary btn-lg">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
              <polyline points="17 8 12 3 7 8" />
              <line x1="12" y1="3" x2="12" y2="15" />
            </svg>
            Start Free — No Credit Card Required
          </Link>
        </div>
      </section>

      {/* Footer */}
      <footer
        style={{
          padding: 'var(--space-2xl) var(--space-xl)',
          borderTop: '1px solid var(--black-700)',
        }}
      >
        <div
          style={{
            maxWidth: 1200,
            margin: '0 auto',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-sm)' }}>
            <div
              style={{
                width: 28,
                height: 28,
                borderRadius: 'var(--radius-sm)',
                background: 'linear-gradient(135deg, var(--cyan-500), var(--purple-500))',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5">
                <polygon points="23 7 16 12 23 17 23 7" />
                <rect x="1" y="5" width="15" height="14" rx="2" ry="2" />
              </svg>
            </div>
            <span style={{ color: 'var(--black-300)', fontSize: '0.875rem' }}>
              © 2025 VideoToCode
            </span>
          </div>

          <div style={{ display: 'flex', gap: 'var(--space-xl)' }}>
            <a href="#" style={{ color: 'var(--black-300)', fontSize: '0.875rem' }}>
              Privacy
            </a>
            <a href="#" style={{ color: 'var(--black-300)', fontSize: '0.875rem' }}>
              Terms
            </a>
            <a href="#" style={{ color: 'var(--black-300)', fontSize: '0.875rem' }}>
              Contact
            </a>
          </div>
        </div>
      </footer>
    </div>
  );
}

// Floating code snippet decoration
function FloatingCodeBlock({ code, style }: { code: string; style: React.CSSProperties }) {
  return (
    <div
      className="animate-float"
      style={{
        position: 'absolute',
        padding: 'var(--space-sm) var(--space-md)',
        background: 'rgba(17, 17, 19, 0.9)',
        border: '1px solid var(--black-600)',
        borderRadius: 'var(--radius-md)',
        fontFamily: 'var(--font-mono)',
        fontSize: '0.75rem',
        color: 'var(--cyan-400)',
        whiteSpace: 'nowrap',
        zIndex: 0,
        opacity: 0.6,
        ...style,
      }}
    >
      {code}
    </div>
  );
}

// Pain point card component
function PainPointCard({
  icon,
  title,
  description,
  visible,
  delay,
}: {
  icon: React.ReactNode;
  title: string;
  description: string;
  visible: boolean;
  delay: number;
}) {
  return (
    <div
      className={visible ? `animate-slide-up stagger-${delay + 1}` : ''}
      style={{
        padding: 'var(--space-xl)',
        background: 'var(--black-700)',
        border: '1px solid var(--black-600)',
        borderRadius: 'var(--radius-lg)',
        opacity: visible ? 1 : 0,
      }}
    >
      <div
        style={{
          width: 48,
          height: 48,
          borderRadius: 'var(--radius-md)',
          background: 'rgba(239, 68, 68, 0.1)',
          border: '1px solid rgba(239, 68, 68, 0.2)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: 'var(--red-400)',
          marginBottom: 'var(--space-lg)',
        }}
      >
        {icon}
      </div>
      <h4 style={{ marginBottom: 'var(--space-sm)' }}>{title}</h4>
      <p style={{ color: 'var(--black-200)', fontSize: '0.9375rem' }}>{description}</p>
    </div>
  );
}

// Step card component
function StepCard({
  number,
  title,
  description,
  icon,
  visible,
  delay,
}: {
  number: number;
  title: string;
  description: string;
  icon: React.ReactNode;
  visible: boolean;
  delay: number;
}) {
  return (
    <div
      className={visible ? `animate-slide-up stagger-${delay + 1}` : ''}
      style={{
        textAlign: 'center',
        position: 'relative',
        zIndex: 1,
        opacity: visible ? 1 : 0,
      }}
    >
      <div
        style={{
          width: 80,
          height: 80,
          margin: '0 auto var(--space-lg)',
          borderRadius: '50%',
          background: 'var(--black-800)',
          border: '2px solid var(--cyan-500)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: 'var(--cyan-400)',
          position: 'relative',
          boxShadow: '0 0 30px var(--cyan-glow)',
        }}
      >
        {icon}
        <div
          style={{
            position: 'absolute',
            top: -8,
            right: -8,
            width: 28,
            height: 28,
            borderRadius: '50%',
            background: 'var(--cyan-500)',
            color: 'var(--black-900)',
            fontSize: '0.875rem',
            fontWeight: 600,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          {number}
        </div>
      </div>
      <h4 style={{ marginBottom: 'var(--space-sm)' }}>{title}</h4>
      <p style={{ color: 'var(--black-200)', fontSize: '0.9375rem', maxWidth: 280, margin: '0 auto' }}>
        {description}
      </p>
    </div>
  );
}

// Pricing card component
function PricingCard({
  name,
  price,
  period,
  description,
  features,
  buttonText,
  buttonVariant,
  highlighted,
  visible,
  delay,
}: {
  name: string;
  price: string;
  period: string;
  description: string;
  features: string[];
  buttonText: string;
  buttonVariant: 'primary' | 'secondary';
  highlighted?: boolean;
  visible: boolean;
  delay: number;
}) {
  return (
    <div
      className={visible ? `animate-slide-up stagger-${delay + 1}` : ''}
      style={{
        padding: 'var(--space-2xl)',
        background: highlighted ? 'var(--black-700)' : 'var(--black-900)',
        border: `1px solid ${highlighted ? 'var(--cyan-500)' : 'var(--black-600)'}`,
        borderRadius: 'var(--radius-xl)',
        position: 'relative',
        opacity: visible ? 1 : 0,
        boxShadow: highlighted ? '0 0 40px var(--cyan-glow)' : 'none',
      }}
    >
      {highlighted && (
        <div
          style={{
            position: 'absolute',
            top: -12,
            left: '50%',
            transform: 'translateX(-50%)',
            padding: 'var(--space-xs) var(--space-md)',
            background: 'var(--cyan-500)',
            color: 'var(--black-900)',
            fontSize: '0.75rem',
            fontWeight: 600,
            borderRadius: 'var(--radius-sm)',
            textTransform: 'uppercase',
            letterSpacing: '0.05em',
          }}
        >
          Most Popular
        </div>
      )}

      <h3 style={{ marginBottom: 'var(--space-xs)' }}>{name}</h3>
      <p style={{ color: 'var(--black-300)', fontSize: '0.875rem', marginBottom: 'var(--space-lg)' }}>
        {description}
      </p>

      <div style={{ display: 'flex', alignItems: 'baseline', marginBottom: 'var(--space-xl)' }}>
        <span style={{ fontSize: '3rem', fontWeight: 700, color: 'white' }}>{price}</span>
        <span style={{ color: 'var(--black-300)', marginLeft: 'var(--space-xs)' }}>{period}</span>
      </div>

      <ul style={{ listStyle: 'none', marginBottom: 'var(--space-xl)' }}>
        {features.map((feature, i) => (
          <li
            key={i}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 'var(--space-sm)',
              padding: 'var(--space-sm) 0',
              color: 'var(--black-100)',
              fontSize: '0.9375rem',
            }}
          >
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="var(--cyan-500)"
              strokeWidth="2"
            >
              <polyline points="20 6 9 17 4 12" />
            </svg>
            {feature}
          </li>
        ))}
      </ul>

      <Link
        to="/dashboard"
        className={`btn ${buttonVariant === 'primary' ? 'btn-primary' : 'btn-secondary'}`}
        style={{ width: '100%' }}
      >
        {buttonText}
      </Link>
    </div>
  );
}
